import React, { memo, useEffect, useMemo } from 'react';
import { Container, Grid, Theme, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { useTranslation } from 'i18n';
import { trackPage } from './ScrenTracker';
import FilterPanel from '../filters/FilterPanel';
import SortFilter from '../filters/SortFilter';
import { OrganizationsSelectors } from '../../store/selectors';
import { useInfinityLoader, useQueryParams } from '../../hooks';
import { OrganizationsActions } from '../../store/actions';
import UserOrgCard from '../common/UserOrgCard/UserOrgCard';
import OrgTypeFilter from '../filters/OrgTypeFilter';
import LocalMarketFilter from '../filters/LocalMarketFilter';

const useStyles = makeStyles()((theme) => ({
  wrapper: {
    paddingBottom: theme.spacing(9),
    [theme.breakpoints.up('xs')]: {
      paddingBottom: '24px',
    },
  },
  root: {
    position: 'relative',
    backgroundColor: 'white',
    [theme.breakpoints.up('md')]: {
      marginLeft: 'auto',
    },
  },
  title: {
    marginTop: 48,
    marginBottom: 16,
    fontFamily: 'Poppins',
    fontWeight: 700,
    fontSize: 24,
    lineHeight: '120%',
    letterSpacing: '-0.02em',
    color: '#383C44',
  },
  scrollContainer: {
    paddingTop: 40,
    [theme.breakpoints.down('md')]: {
      overflowX: 'auto',
      ...theme.mixins.scrollBarOverflowYAuto,
    },
  },
  itemContainer: {
    height: '100%',
    border: '1px solid white',
    background: 'white',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    transform: 'translateY(0) rotateX(0) scale(1)',
    transformOrigin: 'center center',
    opacity: 1,
    transition: 'transform 0.6s cubic-bezier(.1, .3, .2, 1), opacity 0.6s cubic-bezier(.1, .3, .2, 1)',
    '&:hover': {
      transform: 'translateY(0) rotateX(0) scale(1.04)',
      boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
    },
    borderRadius: 16,
    [theme.breakpoints.down('sm')]: {
      borderRadius: 8,
      boxShadow: '0px 4px 8px rgba(56, 60, 68, 0.12)',
    },
    padding: theme.spacing(3, 2),
  },
}));

const isProdEnv = () => process.env.NEXT_PUBLIC_BUILD_ENV === 'production';

const OrganizationsScreen = () => {
  const { classes } = useStyles();
  const [t] = useTranslation('public');

  useEffect(() => {
    trackPage('Organizations Page');
  }, []);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [queryParams] = useQueryParams();

  const organisationsStateSelector = useMemo(() => OrganizationsSelectors.getOrganizationsState(), []);
  const paginationSelector = useMemo(() => OrganizationsSelectors.getOrganizationsPagination, []);

  const hiddenQueryParams = useMemo(
    () => ({
      ordering: queryParams?.ordering || '-event_count',
      is_test: false,
      page_size: isProdEnv() ? 100 : 10,
    }),
    [queryParams?.ordering],
  );

  const {
    state: organizationsState,
    setElementChild,
    setElementWrapper,
  } = useInfinityLoader({
    actions: OrganizationsActions.getOrganizations,
    stateSelector: organisationsStateSelector,
    paginationSelector,
    options: {
      hiddenQueryParams,
      queryParams,
      skipInitialLoad: false,
    },
  });

  useEffect(() => {
    setElementWrapper(window);
    setElementChild(document.documentElement);
  }, [setElementChild, setElementWrapper]);

  return (
    <div className={classes.wrapper}>
      <div className={classes.root}>
        <Container maxWidth="lg">
          <Grid container alignItems="center">
            <Grid item xs={12}>
              <Typography className={classes.title}>{t<string>('screen.organizations.title')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <FilterPanel sort={<SortFilter defaultOrder="-event_count" type="organizations" />}>
                <OrgTypeFilter disabled={organizationsState.isFetching} />
                <LocalMarketFilter disabled={organizationsState.isFetching} />
              </FilterPanel>
            </Grid>
            <Grid item xs={12}>
              <Grid
                container
                spacing={isMobile ? 2 : 3}
                alignContent="space-between"
                className={classes.scrollContainer}
              >
                {organizationsState.entities.map((org) => (
                  <Grid item xs={6} sm={6} md={4} key={org.id}>
                    <div className={classes.itemContainer}>
                      <UserOrgCard title={org.title} id={String(org.id)} logo={org.logo} tagline={org.tagline} />
                    </div>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </div>
    </div>
  );
};

export default memo(OrganizationsScreen);
